import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './lib/auth';
import { trackPageView, identifyUser } from './lib/facebook';
import { isFbFunnelSession } from './lib/funnel';
import './App.css';

// Landing stays eagerly bundled — it's the page anonymous visitors (and
// PageSpeed) actually land on, and lazy-loading it would add a chunk
// round-trip before first paint, working against the FCP/LCP it's judged on.
// Every other route is only needed after that first paint, so it's split
// into its own chunk and fetched on demand.
import Landing from './pages/Landing';
import Spinner from './components/UI/Spinner';

const SignIn = lazy(() => import('./pages/SignIn'));
const SignUp = lazy(() => import('./pages/SignUp'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Library = lazy(() => import('./pages/Library'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Trending = lazy(() => import('./pages/Trending'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Upgrade = lazy(() => import('./pages/Upgrade'));
const Settings = lazy(() => import('./pages/Settings'));
const Admin = lazy(() => import('./pages/Admin'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const WelcomePro = lazy(() => import('./pages/WelcomePro'));
const Discover = lazy(() => import('./pages/Discover'));

function FullScreenSpinner() {
  return <div style={{height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><Spinner size="lg" /></div>;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) {
    // A Discover-funnel visitor clicking Library/Saved/Trending/Settings in
    // the sidebar is almost always a brand-new prospect, not a returning
    // user whose session expired — send them to create an account, not to
    // a sign-in form they have no credentials for. Everywhere else keeps
    // the normal sign-in default.
    return <Navigate to={isFbFunnelSession() ? '/signup' : '/signin'} replace />;
  }
  // onboarding temporarily disabled — users go straight to library
  return children;
}

function PublicRoute({ children, fallback = '/library' }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (user) return <Navigate to={fallback} replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, profile } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/signin" replace />;
  // Client-side check is UX only — the real enforcement is server-side RLS
  // on the ads table and the admin auth check in the edge functions.
  if (profile?.role !== 'admin') return <Navigate to="/library" replace />;
  return children;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => { trackPageView(); }, [location.pathname]);
  return null;
}

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return null;
}

// Applies the saved (or default-dark) theme to <html> on every page load.
// The toggle itself lives in Settings, but that page isn't always mounted —
// without this, the data-theme attribute never gets set on any other page
// and the app silently falls back to the :root (light) CSS variables.
function ThemeInitializer() {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', localStorage.getItem('theme') || 'dark');
  }, []);
  return null;
}

function UserIdentifier() {
  const { user, profile } = useAuth();
  useEffect(() => {
    if (!user) { identifyUser({}); return; }
    const parts = (profile?.full_name || '').trim().split(/\s+/);
    identifyUser({
      email:     user.email,
      userId:    user.id,
      firstName: parts[0] || undefined,
      lastName:  parts.slice(1).join(' ') || undefined,
    });
  }, [user, profile]);
  return null;
}

function AppRoutes() {
  return (
    <>
      <ThemeInitializer />
      <PageViewTracker />
      <UserIdentifier />
      <ScrollToTop />
    <Suspense fallback={<FullScreenSpinner />}>
    <Routes>
      <Route path="/" element={<PublicRoute fallback="/library"><Landing /></PublicRoute>} />
      <Route path="/fb" element={<PublicRoute fallback="/library"><Landing /></PublicRoute>} />
      <Route path="/signin" element={<PublicRoute><SignIn /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/discover" element={<Discover />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/welcome-pro" element={<WelcomePro />} />
      <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/trending" element={<ProtectedRoute><Trending /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#0A0A0A',
              color: '#fff',
              borderRadius: '10px',
              fontSize: '14px',
              padding: '12px 16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            },
            success: { iconTheme: { primary: '#D4590A', secondary: '#fff' } },
            error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
